import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Video, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import InterviewScorecard from "./InterviewScorecard";

const STAT_TONE = {
  "Requested":"bg-slate-100 text-slate-700 border-slate-200",
  "Scheduled":"bg-sky-50 text-sky-700 border-sky-200",
  "Awaiting Scheduling":"bg-sky-50 text-sky-700 border-sky-200",
  "Candidate Declined":"bg-rose-50 text-rose-700 border-rose-200",
  "Completed":"bg-emerald-50 text-emerald-700 border-emerald-200",
  "Advancing":"bg-emerald-50 text-emerald-700 border-emerald-200",
  "Second Interview Requested":"bg-sky-50 text-sky-700 border-sky-200",
  "On Hold":"bg-amber-50 text-amber-800 border-amber-200",
  "Not a Fit":"bg-rose-50 text-rose-700 border-rose-200",
  "Cancelled":"bg-red-50 text-red-700 border-red-200",
  "No Show":"bg-amber-50 text-amber-800 border-amber-200",
};

export default function Interviews() {
  const [rows, setRows] = useState([]);
  const [scoring, setScoring] = useState(null);
  const load = async () => { const {data} = await api.get("/interviews"); setRows(data); };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/interviews/${id}`, {status}); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Interviews</div>
        <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Interview management</h1>
        <p className="text-sm text-slate-500 mt-1">Requests, schedules, feedback and outcomes.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100/70 text-slate-600 text-[11px] uppercase tracking-widest font-semibold">
            <tr><th className="text-left px-4 py-3">Interview</th><th className="text-left px-4 py-3">When</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Meeting</th><th className="text-right px-4 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map(i => (
              <tr key={i.id} data-testid={`interview-row-${i.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2"><div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-slate-500"><Video size={13}/></div>
                    <div><div className="font-semibold text-slate-900 text-xs">Candidate {i.candidate_id?.slice(0,8)}</div><div className="text-[10px] text-slate-500">Job {i.job_order_id?.slice(0,8)}</div></div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{i.scheduled_at ? new Date(i.scheduled_at).toLocaleString() : "TBD"}</td>
                <td className="px-4 py-3 text-slate-700">{i.interview_type}</td>
                <td className="px-4 py-3"><span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded border ${STAT_TONE[i.status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{i.status}</span></td>
                <td className="px-4 py-3">{i.meeting_link ? <a target="_blank" rel="noreferrer" href={i.meeting_link} className="text-sky-700 font-semibold text-xs">Join</a> : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`iv-scheduled-${i.id}`} onClick={()=>setStatus(i.id, "Scheduled")} className="text-[10px] px-2 py-0.5 border border-slate-300 rounded font-semibold hover:bg-slate-50">Scheduled</button>
                    <button data-testid={`iv-completed-${i.id}`} onClick={()=>setStatus(i.id, "Completed")} className="text-[10px] px-2 py-0.5 border border-emerald-300 text-emerald-700 rounded font-semibold hover:bg-emerald-50">Completed</button>
                    <button data-testid={`iv-score-${i.id}`} onClick={()=>setScoring(i)} className="text-[10px] px-2 py-0.5 border border-sky-300 bg-sky-50 text-sky-700 rounded font-semibold hover:bg-sky-100 inline-flex items-center gap-1"><ClipboardCheck size={10}/> Scorecard</button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-sm text-slate-500">No interviews scheduled.</td></tr>}
          </tbody>
        </table>
      </div>
      {scoring && <InterviewScorecard interview={scoring} onClose={()=>setScoring(null)} onSubmitted={load}/>}
    </Layout>
  );
}
